/**
 * Consent Management Service
 *
 * Manages cookie/tracking consent state. All third-party analytics,
 * marketing and functional scripts are gated here — nothing fires
 * until the user explicitly grants permission.
 *
 * Categories:
 *   necessary  — always active, no toggle (auth cookies, session)
 *   analytics  — GA4 (Consent Mode v2), Microsoft Clarity
 *   marketing  — Prodiax Tracking SDK
 *   functional — Hotjar (UX recordings)
 *
 * Policy version bump forces re-consent from all users.
 */

export const POLICY_VERSION = '1.0'
const STORAGE_KEY = 'coridor_consent'

// ─── Internal helpers ────────────────────────────────────────────────────────

function _gtag(...args) {
  if (typeof window.gtag === 'function') {
    window.gtag(...args)
  }
}

function _loadScript(src, attrs = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v))
    s.onload = resolve
    s.onerror = reject
    document.head.appendChild(s)
  })
}

function _loadClarity() {
  if (window.clarity) return
  const id = 'mh5u4w56ed'
  ;(function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
    t = l.createElement(r); t.async = 1
    t.src = 'https://www.clarity.ms/tag/' + i
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y)
  })(window, document, 'clarity', 'script', id)
}

function _loadHotjar() {
  if (window.hj) return
  ;(function (h, o, t, j, a, r) {
    h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) }
    h._hjSettings = { hjid: 3348057, hjsv: 6 }
    a = o.getElementsByTagName('head')[0]
    r = o.createElement('script'); r.async = 1
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv
    a.appendChild(r)
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')
}

function _loadProdiax() {
  if (window.Prodiax) return
  _loadScript('https://unpkg.com/prodiax-tracking-sdk@2.0.14/dist/prodiax-sdk.min.js', {
    integrity: 'sha384-FhGTSC/FLnvYuCrfbE/bMq7jtqgooS45h98bkKi5b5Cfw8/xeUvwGt2kZ/hAGj7H',
    crossorigin: 'anonymous'
  }).then(() => {
    if (window.Prodiax) {
      window.Prodiax.init({ productId: 'coridor.co_a037cb9f7c8742caac8bf242' })
    }
  }).catch(() => {})
}

function _applyGoogleConsentMode(prefs) {
  _gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied'
  })
}

// ─── Public API ──────────────────────────────────────────────────────────────

/** Returns stored consent object or null if user has never decided. */
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/** True if user has made a decision for the current policy version. */
export function hasDecided() {
  const c = getStoredConsent()
  return c !== null && c.policyVersion === POLICY_VERSION
}

/**
 * Applies consent preferences: updates Google Consent Mode and
 * conditionally loads third-party scripts. Safe to call multiple times.
 */
export function applyConsent(prefs) {
  if (process.env.NODE_ENV !== 'production') return

  _applyGoogleConsentMode(prefs)

  if (prefs.analytics) _loadClarity()
  if (prefs.functional) _loadHotjar()
  if (prefs.marketing) _loadProdiax()
}

/** Saves preferences to localStorage and applies them immediately. */
export function savePreferences(prefs) {
  const consent = {
    policyVersion: POLICY_VERSION,
    analytics: Boolean(prefs.analytics),
    marketing: Boolean(prefs.marketing),
    functional: Boolean(prefs.functional),
    decidedAt: new Date().toISOString()
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  } catch {}
  applyConsent(consent)
  return consent
}

/** Accept all categories. */
export function acceptAll() {
  return savePreferences({ analytics: true, marketing: true, functional: true })
}

/** Reject all optional categories. */
export function rejectAll() {
  return savePreferences({ analytics: false, marketing: false, functional: false })
}

/**
 * Call once on app boot. If user already decided for the current policy
 * version, re-applies stored preferences immediately (so returning visitors
 * get their scripts loaded without seeing the banner again).
 */
export function initConsent() {
  const stored = getStoredConsent()
  if (stored && stored.policyVersion === POLICY_VERSION) {
    applyConsent(stored)
  }
}
